import { api } from 'src/boot/axios';
import { defineStore } from 'pinia';

import { TLoginPayload } from 'types';
import { useProfileStore } from './profile-store';
import { useDeathNotificationsStore } from './death-notifications-store';
import { useOrganisationsStore } from './organisations-store';
import { useVisibilityLevelsStore } from './visibility-levels-store';
import { usePermissionsStore } from 'stores/permissions-store';
import { useMembersStore } from 'stores/members-store';
import { useUnitsStore } from 'stores/units-store';
import { useMeetingsStore } from 'stores/meetings-store';
import { useInteractiveGuidesStore } from 'stores/interactive-guides-store';
import { useAnnualReturnsStore } from 'stores/annual-returns-store';
import { trace } from 'src/boot/trace';
import { featureFlagsQuery } from 'src/queries/feature-flags/useFetchFeatureFlagsQuery';
import { useQueryCache } from '@pinia/colada';
import { LoginResponse } from 'src/services/auth';
import { featureFlagKeys } from 'src/queries/feature-flags/keys';

type payload = {
  email: string;
  password: string;
  password_confirmation: string;
  token: string;
};

let refreshTimer: ReturnType<typeof setTimeout> | null = null;

function scheduleRefresh(ttlMinutes: number) {
  if (refreshTimer !== null) {
    clearTimeout(refreshTimer);
  }
  const ms = (ttlMinutes - 15) * 60_000;
  const expiry = Date.now() + ms;
  localStorage.setItem('sessionRefreshAt', expiry.toString());
  refreshTimer = setTimeout(runRefresh, ms);
}

function restoreRefresh(msLeft: number) {
  if (refreshTimer !== null) {
    clearTimeout(refreshTimer);
  }
  trace('Running a refresh in ' + msLeft / 1000 + 'secs from now.');
  refreshTimer = setTimeout(runRefresh, msLeft);
}

async function runRefresh() {
  // Only refresh if user is logged in
  const loggedIn = localStorage.getItem('logged-in') === 'true';
  if (!loggedIn) {
    return;
  }

  try {
    const resp = await api.post('/api/v1/refresh');
    if (resp.data?.ttlMinutes) {
      scheduleRefresh(resp.data.ttlMinutes);
    }
  } catch {
    // Don't reschedule on failure - user may have been logged out
  }
}

type State = {
  superuser: boolean;
  initials: string;
  member: boolean;
  emulating: boolean;
};

export const useAuthStore = defineStore('auth', {
  state: (): State => ({
    superuser: false,
    initials: '',
    member: false,
    emulating: false,
  }),
  actions: {
    // Get/set emulation flag - persists to localStorage so a reload
    // (e.g. the one auth-store.emulate() triggers) preserves it.
    isEmulating(active?: boolean): boolean {
      if (active !== undefined) {
        this.emulating = active;
        localStorage.setItem('emulating', JSON.stringify(active));
      } else if (!this.emulating) {
        const cached = localStorage.getItem('emulating');
        if (cached) {
          this.emulating = JSON.parse(cached);
        }
      }
      return this.emulating;
    },

    // Determine if user is logged in by checking profile
    isLoggedIn(): boolean {
      const loggedInLS = localStorage.getItem('logged-in');
      return loggedInLS === 'true';
    },

    // Check if user is superuser
    isSuperuser(): boolean {
      // If state is false but localStorage has data, load from cache
      if (!this.superuser) {
        const cachedSuperuser = localStorage.getItem('s');
        if (cachedSuperuser) {
          this.superuser = JSON.parse(cachedSuperuser);
        }
      }
      return this.superuser;
    },

    // Get/set member status - stores to cache if provided, otherwise loads from cache
    isMember(isMember?: boolean): boolean {
      if (isMember !== undefined) {
        this.member = isMember;
        localStorage.setItem('is_member', JSON.stringify(isMember));
      } else if (!this.member) {
        const cachedMember = localStorage.getItem('is_member');
        if (cachedMember) {
          this.member = JSON.parse(cachedMember);
        }
      }
      return this.member;
    },

    // Cache superuser setting to localStorage
    cacheSuperuserData(isSuperuser: boolean) {
      this.superuser = isSuperuser;
      localStorage.setItem('s', JSON.stringify(isSuperuser));
    },

    // Load superuser setting from localStorage
    loadSuperuserFromCache() {
      const cachedSuperuser = localStorage.getItem('s');
      if (cachedSuperuser) {
        this.superuser = JSON.parse(cachedSuperuser);
      }
    },

    // Get initials - stores to cache if provided, otherwise loads from cache
    getInitials(initials?: string): string {
      if (initials) {
        this.initials = initials;
        localStorage.setItem('initials', initials);
      } else if (!this.initials) {
        this.initials = localStorage.getItem('initials') ?? '';
      }
      return this.initials;
    },

    setRestoreRefresh(ttlMinutes: number) {
      restoreRefresh(ttlMinutes);
    },

    async revalidateSession(): Promise<boolean> {
      if (!this.isLoggedIn()) {
        return false;
      }

      // Load settings from cache first
      this.loadSuperuserFromCache();
      this.getInitials();
      this.isMember();

      // only treat 200, 401, and 404 as "successful," so they don't trigger the error interceptor
      const resp = await api.get('/api/v1/check-session', {
        validateStatus: (status) =>
          status === 200 || status === 401 || status === 404,
      });

      if (resp.status === 200) {
        if (resp.data?.ttlMinutes) {
          scheduleRefresh(resp.data.ttlMinutes);
        }

        if (resp.data?.permissions) {
          const permissionsStore = usePermissionsStore();
          permissionsStore.setPermissions(
            resp.data?.permissions?.user?.permissions
          );
          permissionsStore.setScheduleRefresh(1);

          // Handle superuser setting
          const superuserValue = resp.data?.permissions?.user?.s ?? 0;
          this.cacheSuperuserData(superuserValue === 1);
        }

        // Store member status from response
        if (resp.data?.is_member !== undefined) {
          this.isMember(resp.data.is_member);
        }

        // Track emulation flag so the layout can flag the session.
        this.isEmulating(resp.data?.emulation?.active === true);

        localStorage.setItem('logged-in', 'true');
        const profileStore = useProfileStore();
        await profileStore.getUser(true);
        await this.precache();
        return true;
      }

      // 401, 404, or anything else → not logged in
      localStorage.setItem('logged-in', 'false');
      return false;
    },

    async establishSession(data: LoginResponse) {
      if (data.ttlMinutes) {
        scheduleRefresh(data.ttlMinutes);
      }

      if (data.permissions) {
        const permissionsStore = usePermissionsStore();
        permissionsStore.setPermissions(data.permissions.user.permissions);
        permissionsStore.setScheduleRefresh(10);

        // Handle superuser setting
        const superuserValue = data.permissions.user.s ?? 0;
        this.cacheSuperuserData(superuserValue === 1);
      }

      // Store initials from login response
      if (data.initials) {
        this.getInitials(data.initials);
      }

      // Store member status from login response
      this.isMember(data.is_member);

      // A fresh login is never an emulation; clear any stale flag.
      this.isEmulating(false);

      localStorage.setItem('logged-in', 'true');

      // Fetch profile to populate store
      const profileStore = useProfileStore();
      await profileStore.getUser(true);

      // Navigate home and preload data
      await this.router.push('/');
      await this.precache();

      await useQueryCache().invalidateQueries({ key: featureFlagKeys.all });
    },

    async emulate(email: string) {
      const resp = await api.post('/api/v1/emulation/start', { email });

      // Invalidate all pinia-colada queries in the cache
      const queryCache = useQueryCache();
      queryCache.invalidateQueries();

      // Reset all per-user Pinia state.
      useProfileStore().clear();
      usePermissionsStore().clear();
      useMembersStore().clear();
      useUnitsStore().clear();
      useMeetingsStore().clear();
      this.superuser = false;
      this.initials = '';
      this.member = false;

      // Wipe localStorage entirely so no superuser-scoped cache (profile,
      // meetings, ranks, office posts, ACL filters, etc.) leaks into the
      // emulated session. We immediately repopulate the keys we need.
      localStorage.clear();
      if (refreshTimer !== null) {
        clearTimeout(refreshTimer);
      }

      if (resp.data?.ttlMinutes) {
        scheduleRefresh(resp.data.ttlMinutes);
      }

      if (resp.data?.permissions) {
        const permissionsStore = usePermissionsStore();
        permissionsStore.setPermissions(
          resp.data?.permissions?.user?.permissions
        );
        permissionsStore.setScheduleRefresh(10);

        const superuserValue = resp.data?.permissions?.user?.s ?? 0;
        this.cacheSuperuserData(superuserValue === 1);
      }

      if (resp.data?.initials) {
        this.getInitials(resp.data.initials);
      }

      if (resp.data?.is_member !== undefined) {
        this.isMember(resp.data.is_member);
      }

      localStorage.setItem('logged-in', 'true');
      this.isEmulating(true);

      // Full reload so directive-based ACL checks (v-can is mounted-only)
      // re-evaluate against the emulated user's permissions. Equivalent
      // to a fresh login from the SPA's perspective.
      window.location.assign('/');
    },

    async forceLogout() {
      localStorage.clear();
      this.superuser = false;
      this.initials = '';
      this.member = false;
      this.emulating = false;
      if (refreshTimer !== null) {
        clearTimeout(refreshTimer);
      }

      // Clear all user-specific stores
      useProfileStore().clear();
      usePermissionsStore().clear();
      useMembersStore().clear();
      useUnitsStore().clear();
      useUnitsStore().clearDataDictionary();
      useMeetingsStore().clear();
      useDeathNotificationsStore().clear();
      useOrganisationsStore().clear();
      useInteractiveGuidesStore().clear();
      useAnnualReturnsStore().clearCachedFilters();

      await this.router.push('/auth/login');

      // Reset every query entry in place rather than removing it: defineQuery
      // keeps hold of its original entry, so removing it would leave the UI
      // reading the previous user's data after the next login. Done after
      // navigating so mounted pages don't render with data suddenly undefined.
      // invalidate() marks entries stale and cancels in-flight fetches without
      // refetching (which would 401).
      const queryCache = useQueryCache();

      queryCache.getEntries().forEach((entry) => {
        queryCache.setEntryState(entry, {
          status: 'pending',
          data: undefined,
          error: null,
        });
        queryCache.invalidate(entry);
      });
    },
    async logout() {
      await api.post('/api/v1/logout');
      await this.forceLogout();
    },

    async precache() {
      if (this.isLoggedIn()) {
        const deathNotificationsStore = useDeathNotificationsStore();
        const organisationsStore = useOrganisationsStore();
        const unitsStore = useUnitsStore();
        const visibilityLevelsStore = useVisibilityLevelsStore();
        const queryCache = useQueryCache();

        const flagsEntry = queryCache.ensure(featureFlagsQuery);

        await Promise.all([
          deathNotificationsStore.getDeathNotifications(),
          organisationsStore.getOrganisations(),
          unitsStore.getDataDictionary(),
          visibilityLevelsStore.getVisibilityLevels(),
          queryCache.refresh(flagsEntry).catch(() => {
            // flags unavailable — features stay off
          }),
        ]);
      }
    },

    async resetPassword(payload: payload) {
      return await api.post('/api/v1/reset-password', payload);
    },

    async requestResetLink(email: string) {
      return await api.post('/api/v1/reset', { email });
    },
  },
});
